import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

export interface GalleryItem {
  id: number;
  customer_visible?: boolean;
  thumbnail?: { url: string };
  thumbnail_processed?: boolean;
  [key: string]: unknown;
}

export interface MediaUpdatePayload {
  id: number;
  job_id: number;
  thumbnail?: { url: string };
  thumbnail_processed?: boolean;
  file?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface UseGalleryActionsOptions<T extends GalleryItem> {
  items: () => T[];
  jobId: () => number | null;
  deleteAction: string;
  visibilityAction: string;
  visibilityParamKey: string; // e.g., 'docId', 'photoId', 'videoId'
  thumbnailEventName: string;
  uploadCompleteEventName?: string;
}

export function useGalleryActions<T extends GalleryItem>(
  options: UseGalleryActionsOptions<T>
) {
  const store = useStore();

  // Delete state
  const deleteId = ref<number | null>(null);
  const deleteConfirm = ref(false);

  // Viewer state
  const viewerItem = ref<T | null>(null) as { value: T | null };

  function handleDelete(id: number) {
    deleteId.value = id;
    deleteConfirm.value = true;
  }

  function toggleDeleteConfirm() {
    deleteConfirm.value = !deleteConfirm.value;
  }

  function closeDeleteConfirm() {
    deleteConfirm.value = false;
    deleteId.value = null;
  }

  async function confirmDelete(): Promise<number | null> {
    const itemId = deleteId.value;
    if (itemId === null) return null;

    await store.dispatch(options.deleteAction, {
      jobId: options.jobId(),
      id: itemId
    });

    closeDeleteConfirm();
    return itemId;
  }

  function handleVisibilityToggle(item: T): Promise<void> {
    const { id, customer_visible } = item;
    const new_visibility = !customer_visible;

    const params: Record<string, unknown> = {
      jobId: options.jobId(),
      [options.visibilityParamKey]: id,
      customer_visible: new_visibility
    };

    return store.dispatch(options.visibilityAction, params).then(() => {
      item.customer_visible = new_visibility;
    });
  }

  function openViewer(item: T) {
    viewerItem.value = item;
  }

  function closeViewer() {
    viewerItem.value = null;
  }

  // Navigation - computed index of current viewer item
  const viewerIndex = computed(() => {
    if (!viewerItem.value) return -1;
    return options.items().findIndex((i) => i.id === viewerItem.value?.id);
  });

  const canGoPrevious = computed(() => viewerIndex.value > 0);

  const canGoNext = computed(() => {
    const items = options.items();
    return viewerIndex.value >= 0 && viewerIndex.value < items.length - 1;
  });

  function goToPrevious() {
    if (!canGoPrevious.value) return;
    const items = options.items();
    viewerItem.value = items[viewerIndex.value - 1];
  }

  function goToNext() {
    if (!canGoNext.value) return;
    const items = options.items();
    viewerItem.value = items[viewerIndex.value + 1];
  }

  function handleMediaUpdate(payload: MediaUpdatePayload) {
    if (payload.job_id !== options.jobId()) return;

    const item = options.items().find((i) => i.id === payload.id);
    if (!item) return;

    // Merge all payload fields into the existing item, excluding identifiers
    const fields = { ...payload };
    delete fields.id;
    delete fields.job_id;
    Object.assign(item, fields);
  }

  // Lifecycle - register EventBus listeners
  onMounted(() => {
    EventBus.$on(options.thumbnailEventName, handleMediaUpdate);
  });

  onBeforeUnmount(() => {
    EventBus.$off(options.thumbnailEventName, handleMediaUpdate);
  });

  return {
    // Delete state
    deleteId,
    deleteConfirm,
    handleDelete,
    toggleDeleteConfirm,
    closeDeleteConfirm,
    confirmDelete,

    // Visibility
    handleVisibilityToggle,

    // Viewer
    viewerItem,
    openViewer,
    closeViewer,

    // Navigation
    viewerIndex,
    canGoPrevious,
    canGoNext,
    goToPrevious,
    goToNext
  };
}
